import { useMemo, useRef, useCallback, useState, useEffect } from 'react'
import { zoom as zoomBehavior, zoomIdentity } from 'd3-zoom'
import { select, event } from 'd3-selection'

import * as THREE from 'three'
import { useThree } from 'react-three-fiber'

const defaultPositions = { x: 0, y: 0 }
const defaultZoom = 1

export default function useZoom (defaultCameraPos = defaultPositions, defaultCameraZoom = defaultZoom) {
  const ref = useRef(null)
  const isZooming = useRef(false)
  const zoom = useRef({
    ...defaultCameraPos,
    k: defaultCameraZoom,
    zooming: isZooming.current
  })
  const [, setZoom] = useState(zoom.current)

  const d3Zoom = useMemo(() => {
    return zoomBehavior()
      .extent([[-1000, -1000], [1000, 1000]])
      .scaleExtent([0.5, 10])
      .on('start', () => {
        isZooming.current = true
      })
      .on('end', () => {
        isZooming.current = false

        zoom.current = {
          x: event.transform.x,
          y: event.transform.y,
          k: event.transform.k,
          zooming: isZooming.current
        }
      })
      .on('zoom', () => {
        zoom.current = {
          x: event.transform.x,
          y: event.transform.y,
          k: event.transform.k,
          zooming: isZooming.current

        }

        setZoom(zoom.current)
      })
  }, [])

  const setRef = useCallback((node) => {
    const bb = node
      ? node.getBoundingClientRect()
      : { width: 0, height: 0 }

    const transform = zoomIdentity
      .scale(
        defaultCameraZoom.k
      )
      .translate(
        defaultCameraPos.x + -(bb.width - (bb.width / defaultCameraZoom.k)) * 0.5,
        defaultCameraPos.y + -(bb.height - (bb.height / defaultCameraZoom.k)) * 0.5
      )

    select(node)
      .call(d3Zoom)

    select(node)
      .transition()
      .duration(850)
      .call(d3Zoom.transform, transform)

    ref.current = node
  }, [d3Zoom, defaultCameraZoom, defaultCameraPos])

  return [setRef, zoom.current]
}

function toDegrees (angle) {
  return angle * (180 / Math.PI)
}

function neededFov (vizHeight, cameraZ, scale) {
  const fovHeight = vizHeight / scale
  const halfFovRadians = Math.atan(fovHeight / (2 * cameraZ))
  const halfFov = toDegrees(halfFovRadians)
  return halfFov * 2
}

function getCameraZ (zoomLevel, vizHeight) {
  return vizHeight / (Math.exp(zoomLevel)
  )
}

export function CameraZoom ({ zoom, mouse }) {
  const { gl, camera } = useThree()

  const size = useEffect(() => {
    gl.sortObjects = false
    if (!(!process.env.NODE_ENV || process.env.NODE_ENV === 'development')) {
      gl.debug.checkShaderErrors = false
    }
  }, [gl])

  useEffect(() => {
    camera.lookAt(new THREE.Vector3(0, 0, 0))
    camera.updateProjectionMatrix()
  }, [camera])

  useEffect(() => {
    const scale = zoom.k
    const size = gl.getSize(new THREE.Vector2())
    const vizHeight = size.y

    camera.position.x = -(size.x * 0.5) - (zoom.x - size.x / 2) / scale
    camera.position.y = (size.y * 0.5) + (zoom.y - vizHeight / 2) / scale
    camera.position.z = getCameraZ(Math.log(scale), vizHeight)
    camera.fov = neededFov(vizHeight, camera.position.z, scale)

    camera.updateProjectionMatrix()
  }, [size, gl, camera, zoom])

  return null
}
